import { McpConnectionStatus } from '@kanbu/schema/enums';
import { useLingui, Trans } from '@lingui/react/macro';
import { Badge, Button, toast } from '@utima/ui';
import { useEffect } from 'react';

import { trpc } from '@/services/trpc';

import { useMcpOAuthConnect } from '../hooks/useMcpOAuthConnect';

export interface McpOAuthPanelProps {
  integrationId: string;
  initialStatus: McpConnectionStatus;
}

export function McpOAuthPanel({
  integrationId,
  initialStatus,
}: McpOAuthPanelProps) {
  const { t } = useLingui();
  const utils = trpc.useUtils();

  const { data: status, refetch } =
    trpc.mcpIntegrations.getOAuthStatus.useQuery({ id: integrationId });
  const { connect, isConnecting } = useMcpOAuthConnect();
  const disconnect = trpc.mcpIntegrations.disconnectOAuth.useMutation({
    onSuccess: async () => {
      await Promise.all([
        refetch(),
        utils.mcpIntegrations.findAll.invalidate(),
      ]);
    },
  });

  const connectionStatus = status?.connectionStatus ?? initialStatus;
  const isConnected = connectionStatus === McpConnectionStatus.Connected;

  const refresh = () =>
    Promise.all([refetch(), utils.mcpIntegrations.findAll.invalidate()]);

  // The OAuth callback popup posts its outcome back to this window before
  // closing, so a backend failure surfaces instead of silently disappearing.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.data?.type !== 'mcp-oauth'
      ) {
        return;
      }

      if (event.data.oauth === 'success') {
        toast.success(t`Connected.`);
      } else {
        toast.error(event.data.message || t`Connect failed.`);
      }

      void refresh();
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='space-y-2 rounded-radius border border-border p-3'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium'>
            <Trans>OAuth connection</Trans>
          </span>
          <Badge variant={isConnected ? 'success' : 'muted'} size='xs'>
            {connectionStatus}
          </Badge>
        </div>
        {isConnected ? (
          <Button
            size='sm'
            variant='ghost'
            loading={disconnect.isPending}
            onClick={() => disconnect.mutate({ id: integrationId })}
          >
            <Trans>Disconnect</Trans>
          </Button>
        ) : (
          <Button
            size='sm'
            loading={isConnecting}
            onClick={() => connect(integrationId)}
          >
            <Trans>Connect</Trans>
          </Button>
        )}
      </div>

      {status?.oauthScopes && status.oauthScopes.length > 0 && (
        <p className='text-xs text-muted-fg'>
          <Trans>Scopes</Trans>: {status.oauthScopes.join(', ')}
        </p>
      )}

      {status?.lastAuthError && (
        <p className='text-xs text-danger'>{status.lastAuthError}</p>
      )}

      {!isConnected && (
        <p className='text-xs text-muted-fg'>
          <Trans>You will sign in when the MCP server requires access.</Trans>
        </p>
      )}
    </div>
  );
}
